import { Box, Card, CardContent, Grid, Typography, Chip } from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  getDashboardAnalytics,
  getTopFiveAnalytics,
  paymentAnalytics,
  payoutSats,
} from "../../../api/services/api";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
  EnhancedAnalyticsCard,
  PageLayout,
  CardSkeleton,
} from "../../../components/Enhanced";

export const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useQuery("admin-analytics", getDashboardAnalytics);

  const {
    data: topFive,
    isLoading: topFiveLoading,
    refetch: refetchTopFive,
  } = useQuery("top-five-analytics", getTopFiveAnalytics);

  const {
    data: payments,
    isLoading: paymentsLoading,
    refetch: refetchPayments,
  } = useQuery("payment-analytics", paymentAnalytics);

  const {
    data: payouts,
    isLoading: payoutsLoading,
    refetch: refetchPayouts,
  } = useQuery("payout-stats", payoutSats);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "SAR",
    }).format(amount);
  };

  const isLoading =
    analyticsLoading || topFiveLoading || paymentsLoading || payoutsLoading;

  // Map backend data structure to frontend display structure
  const analyticsData = {
    totalPatients: analytics?.data?.user?.patients || 0,
    totalClinicians: analytics?.data?.clinician?.clinicians || 0,
    totalSessions: analytics?.data?.session?.total || 0,
    completedSessions:
      analytics?.data?.session?.statuses?.find(
        (s: any) => s.title === "COMPLETED"
      )?.count || 0,
    upcomingSessions:
      analytics?.data?.session?.statuses?.find(
        (s: any) => s.title === "PAID"
      )?.count || 0,
    cancelledSessions:
      analytics?.data?.session?.statuses?.find(
        (s: any) => s.title === "CANCELLED"
      )?.count || 0,
    inProgressSessions:
      analytics?.data?.session?.statuses?.find((s: any) => s.title === "PAID")
        ?.count || 0,
  };

  const paymentData = {
    totalRevenue: payments?.data?.totalPayments || 0,
    totalReceived: payments?.data?.totalPayments || 0,
    platformRevenue: payments?.data?.alsanPayments || 0,
    pendingPayments: payments?.data?.pendingPayouts || 0,
  };

  const payoutData = {
    totalPayouts: payouts?.data?.totalPayout || 0,
    pendingAmount: payouts?.data?.pendingPayout || 0,
    completedAmount: payouts?.data?.clinicianPayout || 0,
    alsanCommission: payouts?.data?.alsanCommision || 0,
  };

  const handleRefreshAll = () => {
    refetchAnalytics();
    refetchTopFive();
    refetchPayments();
    refetchPayouts();
  };

  return (
    <PageLayout
      title="Analytics Dashboard"
      subtitle="Real-time overview of system performance and key metrics"
      breadcrumbs={[
        { label: "Dashboard", path: "/admin/dashboard" },
        { label: "Analytics" },
      ]}
      actions={[
        {
          label: "User Behavior Analytics",
          onClick: () => navigate("/user-analytics"),
          variant: "contained",
          icon: <TimelineIcon />,
        },
        {
          label: "Refresh All",
          onClick: handleRefreshAll,
          variant: "outlined",
          icon: <RefreshIcon />,
        },
      ]}
    >
      {/* Main Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <CardSkeleton variant="analytics" count={1} />
          ) : (
            <EnhancedAnalyticsCard
              title="Total Patients"
              value={analyticsData.totalPatients}
              icon={<PeopleIcon />}
              color="primary"
              tooltip="Total number of registered patients"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <CardSkeleton variant="analytics" count={1} />
          ) : (
            <EnhancedAnalyticsCard
              title="Total Clinicians"
              value={analyticsData.totalClinicians}
              icon={<LocalHospitalIcon />}
              color="secondary"
              tooltip="Total number of active clinicians"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <CardSkeleton variant="analytics" count={1} />
          ) : (
            <EnhancedAnalyticsCard
              title="Total Sessions"
              value={analyticsData.totalSessions}
              icon={<EventIcon />}
              color="success"
              tooltip="Total number of sessions scheduled"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <CardSkeleton variant="analytics" count={1} />
          ) : (
            <EnhancedAnalyticsCard
              title="Total Revenue"
              value={paymentData.totalRevenue}
              icon={<AttachMoneyIcon />}
              color="warning"
              currency={true}
              tooltip="Total revenue generated"
            />
          )}
        </Grid>
      </Grid>

      {/* Session Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" mb={3} fontWeight={600}>
                Session Status Overview
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{
                    backgroundColor: "success.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "success.light",
                  }}
                >
                  <Typography fontWeight={500}>Completed Sessions</Typography>
                  <Chip
                    label={analyticsData.completedSessions || 0}
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{
                    backgroundColor: "primary.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "primary.light",
                  }}
                >
                  <Typography fontWeight={500}>Upcoming Sessions</Typography>
                  <Chip
                    label={analyticsData.upcomingSessions || 0}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{
                    backgroundColor: "error.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "error.light",
                  }}
                >
                  <Typography fontWeight={500}>Cancelled Sessions</Typography>
                  <Chip
                    label={analyticsData.cancelledSessions || 0}
                    color="error"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{
                    backgroundColor: "warning.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "warning.light",
                  }}
                >
                  <Typography fontWeight={500}>In Progress</Typography>
                  <Chip
                    label={analyticsData.inProgressSessions || 0}
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" mb={3} fontWeight={600}>
                Financial Overview
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{ backgroundColor: "grey.50", borderRadius: 1 }}
                >
                  <Typography fontWeight={500}>
                    Total Payments Received
                  </Typography>
                  <Typography fontWeight="bold" variant="h6">
                    {formatCurrency(paymentData.totalReceived || 0)}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{ backgroundColor: "warning.lighter", borderRadius: 1 }}
                >
                  <Typography fontWeight={500}>Pending Payouts</Typography>
                  <Typography
                    fontWeight="bold"
                    variant="h6"
                    color="warning.main"
                  >
                    {formatCurrency(payoutData.pendingAmount || 0)}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{ backgroundColor: "success.lighter", borderRadius: 1 }}
                >
                  <Typography fontWeight={500}>Completed Payouts</Typography>
                  <Typography
                    fontWeight="bold"
                    variant="h6"
                    color="success.main"
                  >
                    {formatCurrency(payoutData.completedAmount || 0)}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  sx={{ backgroundColor: "primary.lighter", borderRadius: 1 }}
                >
                  <Typography fontWeight={500}>Platform Revenue</Typography>
                  <Typography
                    fontWeight="bold"
                    variant="h6"
                    color="primary.main"
                  >
                    {formatCurrency(paymentData.platformRevenue || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performers */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" mb={2} fontWeight={600}>
                Top 5 Clinicians by Sessions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {topFive?.data?.clinicians
                  ?.slice(0, 5)
                  .map((clinician: any, index: number) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      p={2}
                      sx={{
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.200",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "grey.100",
                          borderColor: "primary.main",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "primary.main",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography fontWeight={500}>
                          {clinician.user?.fullName ||
                            clinician.users?.fullName}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${clinician.totalSessions} sessions`}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" mb={2} fontWeight={600}>
                Top 5 Patients by Sessions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {topFive?.data?.patientsProfiles
                  ?.slice(0, 5)
                  .map((patient: any, index: number) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      p={2}
                      sx={{
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.200",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "grey.100",
                          borderColor: "secondary.main",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "secondary.main",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography fontWeight={500}>
                          {patient.fullName}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${patient.totalSessions} sessions`}
                        size="small"
                        color="secondary"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageLayout>
  );
};
